import { useKftLocalStorage } from '~/composables/useKftLocalStorage'

interface OfferPage {
  id: string
  url: string
  timestamp: number
  eventDescriptionId?: string // for linking to items in cart
  signature?: string // signature parameter from query string
  // Event data for display
  title?: string
  image?: string
  firstDate?: string // ISO date string of first available date
  lastDate?: string // ISO date string of last available date for booking
  totalDates?: number // total number of available dates
  location?: string // city name
  requestedTickets?: number // number of requested tickets for this offer
  price?: number // price in cents
  priceCurrency?: string
}

// interface OfferTracking {
//   activeOffers: OfferPage[]
// }

const STORAGE_KEY = 'activeOfferPages'

export const useOfferTracking = () => {
  const { data: activeOffers } = useKftLocalStorage<OfferPage[]>(
    STORAGE_KEY,
    []
  )

  const addOfferPage = (
    offerId: string,
    url: string,
    eventDescriptionId?: string,
    signature?: string,
    eventData?: {
      title?: string
      image?: string
      firstDate?: string
      lastDate?: string
      totalDates?: number
      location?: string
      requestedTickets?: number
      price?: number
      priceCurrency?: string
    }
  ) => {
    const existingOfferIndex = activeOffers.value.findIndex(
      (offer) => offer.id === offerId
    )

    const newOffer: OfferPage = {
      id: offerId,
      url,
      timestamp: Date.now(),
      eventDescriptionId,
      signature,
      ...eventData,
    }

    if (existingOfferIndex >= 0) {
      // update existing offer
      activeOffers.value[existingOfferIndex] = newOffer
    } else {
      // add new offer
      activeOffers.value.push(newOffer)
    }

    return activeOffers.value
  }

  const removeOfferPage = (offerId: string) => {
    activeOffers.value = activeOffers.value.filter(
      (offer) => offer.id !== offerId
    )
    return activeOffers.value
  }

  const clearAllOffers = () => {
    activeOffers.value = []
  }

  const getActiveOffers = (): OfferPage[] => {
    return activeOffers.value
  }

  const getOfferById = (offerId: string): OfferPage | undefined => {
    return activeOffers.value.find((offer) => offer.id === offerId)
  }

  const getOfferByEventDescriptionId = (
    eventDescriptionId: string
  ): OfferPage | undefined => {
    return activeOffers.value.find(
      (offer) => offer.eventDescriptionId === eventDescriptionId
    )
  }

  const isOfferActive = (offerId: string): boolean => {
    return getOfferById(offerId) !== undefined
  }

  // cleanup expired offers (older than 24 hours)
  const cleanupExpiredOffers = () => {
    const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000
    activeOffers.value = activeOffers.value.filter(
      (offer) => offer.timestamp > twentyFourHoursAgo
    )
    return activeOffers.value
  }

  return {
    activeOffers: computed(() => activeOffers.value),
    addOfferPage,
    removeOfferPage,
    clearAllOffers,
    getActiveOffers,
    getOfferById,
    getOfferByEventDescriptionId,
    isOfferActive,
    cleanupExpiredOffers,
  }
}
